import TextareaAutosize from 'react-textarea-autosize';
import { Button } from '../../Utility/Button';
import Input from '../../Utility/FormElements/Input';
import Select from '../../Utility/FormElements/Select';
import AdminContainer from '../../Utility/AdminContainer';
import usePromptFunction from '../../../Hooks/usePromptFunction';
import AdminBox from '../../Utility/AdminBox';
import ImageUpload from '../../Utility/ImageUpload';
import { CodeFunction } from '../../../Types';
import AdminBoxHeadline from '../../Utility/AdminBoxHeadline';
import OpenApiSelector from './OpenApiSelector';
import McpToolSelector from './McpToolSelector';

function PromptFunction() {
    const {
        PromptFunctionActions,
        promptFunction,
        i18n,
        imagePreview,
        codeFunctions,
        possibleOptions,
        selectedOptions,
        possibleArguments,
        selectedArguments,
        promptFunctionLoaded,
        promptFunctionSnapshot,
        promptFunctionIdentifier,
        handleChangePromptFunction,
        setSelectedOptions,
        setSelectedArguments,
        openApiConfigs,
        mcpConfigs,
    } = usePromptFunction();

    const toggleEndpointSelection = (endpointId: string) => {
        const currentEndpoints = promptFunction.openApiEndpoints || [];
        const newEndpoints = currentEndpoints.includes(endpointId)
            ? currentEndpoints.filter((id) => id !== endpointId)
            : [...currentEndpoints, endpointId];
        handleChangePromptFunction('openApiEndpoints', newEndpoints);
    };

    const toggleMcpToolSelection = (toolId: string) => {
        const currentTools = promptFunction.mcpTools || [];
        const newTools = currentTools.includes(toolId)
            ? currentTools.filter((id) => id !== toolId)
            : [...currentTools, toolId];
        handleChangePromptFunction('mcpTools', newTools);
    };

    return (
        <div className={`admin-container flex relative flex-col`}>
            <AdminContainer>
                <AdminBox
                    contentLoaded={
                        promptFunctionLoaded.codeFunctionsLoaded &&
                        promptFunctionLoaded.promptFunctionLoaded &&
                        promptFunctionLoaded.openApiLoaded &&
                        promptFunctionLoaded.mcpLoaded
                    }
                >
                    {promptFunction.title !== 'undefined' &&
                        promptFunction.description !== 'undefined' &&
                        promptFunction.name !== 'undefined' &&
                        codeFunctions && (
                            <div className="flex flex-col gap-5">
                                <AdminBoxHeadline
                                    headLine={i18n.translate({
                                        id: promptFunctionIdentifier === 'new' ? 'newConnector' : 'editConnector',
                                        sourceName: 'PromptFunction',
                                    })}
                                    subLine={
                                        promptFunctionIdentifier === 'new' ? undefined : promptFunctionSnapshot?.title
                                    }
                                />

                                <ImageUpload
                                    imagePreview={imagePreview}
                                    camRemoveImage={false}
                                    handleImageChange={(e) => PromptFunctionActions.handleImageChange(e)}
                                >
                                    <Input
                                        inputType="text"
                                        value={promptFunction.title ?? ''}
                                        onChange={(value) => handleChangePromptFunction('title', value)}
                                        placeholder={i18n.translate({ id: 'placeholder.title', sourceName: 'Model' })}
                                        className="w-full"
                                    />
                                </ImageUpload>

                                <label className="flex flex-col gap-2">
                                    {i18n.translate({ id: 'title.description', sourceName: 'PromptFunction' })}
                                    <TextareaAutosize
                                        className="bg-input-backgroundColor w-full p-3 border border-2 border-input-borderColor rounded-lg outline-none focus:border-primary transition-all duration-100"
                                        value={promptFunction.description ?? ''}
                                        onChange={(e) => handleChangePromptFunction('description', e.target.value)}
                                        minRows={2}
                                        maxRows={5}
                                    />
                                </label>

                                <label className="flex flex-col gap-2">
                                    {i18n.translate({ id: 'title.connectorType', sourceName: 'PromptFunction' })}
                                    <Select
                                        value={promptFunction.type ?? 'code'}
                                        onChange={(value) => handleChangePromptFunction('type', value)}
                                    >
                                        <option value="code">
                                            {i18n.translate({ id: 'type.code', sourceName: 'PromptFunction' })}
                                        </option>
                                        <option value="openapi">
                                            {i18n.translate({ id: 'type.openapi', sourceName: 'PromptFunction' })}
                                        </option>
                                        <option value="mcp">
                                            {i18n.translate({ id: 'type.mcp', sourceName: 'PromptFunction' })}
                                        </option>
                                    </Select>
                                </label>

                                {promptFunction.type === 'code' && (
                                    <>
                                        <label className="flex flex-col gap-2">
                                            {i18n.translate({
                                                id: 'title.functionInCode',
                                                sourceName: 'PromptFunction',
                                            })}
                                            <Select
                                                value={promptFunction.name ?? '---'}
                                                onChange={PromptFunctionActions.handleCodeFunctionDropdownChange}
                                            >
                                                <option key="---" value="---">
                                                    {i18n.translate({ id: 'option', sourceName: 'Main' })}
                                                </option>
                                                {codeFunctions &&
                                                    codeFunctions.map((codeFunction: CodeFunction) => {
                                                        return (
                                                            <option key={codeFunction.name} value={codeFunction.name}>
                                                                {i18n.translate({
                                                                    id:
                                                                        'codeFunction.' +
                                                                        codeFunction.name
                                                                            .replaceAll('\\', '_')
                                                                            .replace('::', '-'),
                                                                    sourceName: 'PromptFunction',
                                                                })}
                                                            </option>
                                                        );
                                                    })}
                                            </Select>
                                        </label>

                                        {possibleOptions.length > 0 && (
                                            <div>
                                                <h5 className="my-5">
                                                    {i18n.translate({
                                                        id: 'title.possibleOption',
                                                        sourceName: 'PromptFunction',
                                                    })}
                                                </h5>
                                                <div className="flex flex-col gap-5">
                                                    {possibleOptions.map(([optionKey, optionData]: [string, any]) => {
                                                        const handleInputChange = (value: string) => {
                                                            setSelectedOptions((prev: any) => ({
                                                                ...prev,
                                                                [optionKey]: value,
                                                            }));
                                                        };
                                                        return (
                                                            <div
                                                                key={optionKey}
                                                                className="promptFunction-input-wrapper"
                                                            >
                                                                <label className="flex flex-col gap-2 capitalize">
                                                                    <span>
                                                                        {optionKey}
                                                                        {!(optionData as any).required && (
                                                                            <span
                                                                                style={{
                                                                                    color: 'grey',
                                                                                    marginLeft: '5px',
                                                                                }}
                                                                            >
                                                                                (
                                                                                {i18n.translate({
                                                                                    id: 'title.optional',
                                                                                    sourceName: 'PromptFunction',
                                                                                })}
                                                                                )
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                    <Input
                                                                        inputType="text"
                                                                        required={(optionData as any).required.required}
                                                                        placeholder={optionKey}
                                                                        value={selectedOptions[optionKey] || ''}
                                                                        onChange={handleInputChange}
                                                                    />
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        {possibleArguments.length > 0 && (
                                            <div>
                                                <h5 className="my-5">
                                                    {i18n.translate({
                                                        id: 'title.arguments',
                                                        sourceName: 'PromptFunction',
                                                    })}
                                                </h5>
                                                <div className="flex flex-col gap-5">
                                                    {possibleArguments.map(([argKey, argData]) => {
                                                        const handleInputChange = (value: string) => {
                                                            setSelectedArguments((prev: any) => ({
                                                                ...prev,
                                                                [argKey]: {
                                                                    ...prev[argKey],
                                                                    description: value,
                                                                },
                                                            }));
                                                        };
                                                        return (
                                                            <div key={argKey} className="promptFunction-input-wrapper">
                                                                <label className="flex flex-col gap-2 capitalize">
                                                                    <span>
                                                                        {argKey}
                                                                        {!(argData as any).required && (
                                                                            <span
                                                                                style={{
                                                                                    color: 'grey',
                                                                                    marginLeft: '5px',
                                                                                }}
                                                                            >
                                                                                (
                                                                                {i18n.translate({
                                                                                    id: 'title.optional',
                                                                                    sourceName: 'PromptFunction',
                                                                                })}
                                                                                )
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                    <Input
                                                                        inputType="text"
                                                                        placeholder={argKey}
                                                                        value={
                                                                            selectedArguments[argKey]?.description || ''
                                                                        }
                                                                        onChange={handleInputChange}
                                                                    />
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {promptFunction.type === 'openapi' && (
                                    <div>
                                        <h5 className="my-5">
                                            {i18n.translate({
                                                id: 'title.openApiEndpoints',
                                                sourceName: 'PromptFunction',
                                            })}
                                        </h5>
                                        <p className="text-sm text-gray-500 mb-4">
                                            {i18n.translate({
                                                id: 'description.openApiEndpoints',
                                                sourceName: 'PromptFunction',
                                            })}
                                        </p>

                                        <OpenApiSelector
                                            openApiConfigs={openApiConfigs}
                                            selectedEndpoints={promptFunction.openApiEndpoints || []}
                                            onToggleEndpoint={toggleEndpointSelection}
                                            i18n={i18n}
                                        />
                                    </div>
                                )}

                                {promptFunction.type === 'mcp' && (
                                    <div>
                                        <h5 className="my-5">
                                            {i18n.translate({
                                                id: 'title.mcpTools',
                                                sourceName: 'PromptFunction',
                                            })}
                                        </h5>
                                        <p className="text-sm text-gray-500 mb-4">
                                            {i18n.translate({
                                                id: 'description.mcpTools',
                                                sourceName: 'PromptFunction',
                                            })}
                                        </p>

                                        <McpToolSelector
                                            mcpConfigs={mcpConfigs}
                                            selectedTools={promptFunction.mcpTools || []}
                                            onToggleTool={toggleMcpToolSelection}
                                            i18n={i18n}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    <div className="mt-5 text-right">
                        <Button variant={'primary'} onClick={PromptFunctionActions.handleSave}>
                            {i18n.translate({ id: 'save', sourceName: 'Main' })}
                        </Button>
                    </div>
                </AdminBox>
            </AdminContainer>
        </div>
    );
}

export default PromptFunction;
